import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { lazy } from "react";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/feature/AdminLayout";
import ProtectedRoute from "@/components/feature/ProtectedRoute";

// Admin login — directly imported for instant rendering
import AdminLoginPage from "@/pages/admin/login/page";

// Admin pages — lazy loaded for instant router speed
const DashboardPage = lazy(() => import("@/pages/admin/dashboard/page"));
const StoreAdminPage = lazy(() => import("@/pages/store/admin/page"));
const BlogAdminPage = lazy(() => import("@/pages/blog/admin/page"));
const OrdersAdminPage = lazy(() => import("@/pages/admin/orders/page"));
const ManageUsersPage = lazy(() => import("@/pages/admin/users/page"));
const MediaLibraryPage = lazy(() => import("@/pages/admin/media/page"));
const AnnouncementsPage = lazy(() => import("@/pages/admin/announcements/page"));
const FormsCollectionPage = lazy(() => import("@/pages/admin/forms/page"));
const DomainPage = lazy(() => import("@/pages/admin/domain/page"));
const SeoPage = lazy(() => import("@/pages/admin/seo/page"));
const SeoAnalysisPage = lazy(() => import("@/pages/admin/seo-analysis/page"));
const AnalyticsPage = lazy(() => import("@/pages/admin/analytics/page"));
const SocialPage = lazy(() => import("@/pages/admin/social/page"));
const IntegrationsPage = lazy(() => import("@/pages/admin/integrations/page"));
const PaymentsPage = lazy(() => import("@/pages/admin/payments/page"));
const DeliveryPage = lazy(() => import("@/pages/admin/delivery/page"));
const BillingPage = lazy(() => import("@/pages/admin/billing/page"));
const WebhooksPage = lazy(() => import("@/pages/admin/webhooks/page"));
const SystemStatusPage = lazy(() => import("@/pages/admin/system-status/page"));
const ExportPage = lazy(() => import("@/pages/admin/export/page"));
const ImportPage = lazy(() => import("@/pages/admin/import/page"));
const BackupPage = lazy(() => import("@/pages/admin/backup/page"));
const CronPage = lazy(() => import("@/pages/admin/cron/page"));
const ResetPage = lazy(() => import("@/pages/admin/reset/page"));
const ComparePage = lazy(() => import("@/pages/admin/compare/page"));
const BackendExportPage = lazy(() => import("@/pages/admin/backend-export/page"));
const WebEditorPage = lazy(() => import("@/pages/admin/web-editor/page"));
const ApiKeysPage = lazy(() => import("@/pages/admin/api-keys/page"));
const StoreSettingsPage = lazy(() => import("@/pages/admin/store-settings/page"));
const MigratePage = lazy(() => import("@/pages/admin/migrate/page"));

function AdminShell() {
  return (
    <ProtectedRoute>
      <AdminLayout />
    </ProtectedRoute>
  );
}

function AdminRootRedirect() {
  const { user } = useAuth();
  return <Navigate to={user ? "/dashboard" : "/login"} replace />;
}

const adminRoutes: RouteObject[] = [
  { path: "/", element: <AdminRootRedirect /> },
  { path: "/login", element: <AdminLoginPage /> },

  {
    element: <AdminShell />,
    children: [
      { path: "dashboard", element: <DashboardPage /> },
      { path: "orders", element: <OrdersAdminPage /> },
      { path: "store", element: <StoreAdminPage /> },
      { path: "store/settings", element: <StoreSettingsPage /> },
      { path: "blog", element: <BlogAdminPage /> },
      { path: "media", element: <MediaLibraryPage /> },
      { path: "announcements", element: <AnnouncementsPage /> },
      { path: "forms", element: <FormsCollectionPage /> },
      { path: "settings/seo", element: <SeoPage /> },
      { path: "settings/domain", element: <DomainPage /> },
      { path: "settings/integrations", element: <IntegrationsPage /> },
      { path: "settings/payments", element: <PaymentsPage /> },
      { path: "settings/delivery", element: <DeliveryPage /> },
      { path: "settings/billing", element: <BillingPage /> },
      { path: "settings/social", element: <SocialPage /> },
      { path: "settings/users", element: <ManageUsersPage /> },
      { path: "settings/webhooks", element: <WebhooksPage /> },
      { path: "settings/api-keys", element: <ApiKeysPage /> },
      { path: "analytics", element: <AnalyticsPage /> },
      { path: "seo-analysis", element: <SeoAnalysisPage /> },
      { path: "system-status", element: <SystemStatusPage /> },
      { path: "export", element: <ExportPage /> },
      { path: "import", element: <ImportPage /> },
      { path: "backups", element: <BackupPage /> },
      { path: "cron", element: <CronPage /> },
      { path: "backend-export", element: <BackendExportPage /> },
      { path: "migrate", element: <MigratePage /> },
      { path: "compare", element: <ComparePage /> },
      { path: "reset", element: <ResetPage /> },
      { path: "web-editor", element: <WebEditorPage /> },

      // Legacy redirects inside admin shell
      { path: "seo", element: <Navigate to="/settings/seo" replace /> },
      { path: "domain", element: <Navigate to="/settings/domain" replace /> },
      { path: "integrations", element: <Navigate to="/settings/integrations" replace /> },
      { path: "payments", element: <Navigate to="/settings/payments" replace /> },
      { path: "delivery", element: <Navigate to="/settings/delivery" replace /> },
      { path: "billing", element: <Navigate to="/settings/billing" replace /> },
      { path: "social", element: <Navigate to="/settings/social" replace /> },
      { path: "users", element: <Navigate to="/settings/users" replace /> },
      { path: "webhooks", element: <Navigate to="/settings/webhooks" replace /> },
      { path: "api-keys", element: <Navigate to="/settings/api-keys" replace /> },
      { path: "backup", element: <Navigate to="/backups" replace /> },
      { path: "cms", element: <Navigate to="/web-editor" replace /> },
      { path: "theme", element: <Navigate to="/web-editor" replace /> },

      { path: "*", element: <Navigate to="/dashboard" replace /> },
    ],
  },

  { path: "*", element: <Navigate to="/login" replace /> },
];

export default adminRoutes;